import { message } from 'antd';
import moment from 'moment';
import i18n from '@/i18n';
import { DEFAULT_MINUTE_FORMATTER, DEFAULT_FORMATTER, getColumnString, getCompareItemId } from '@/presentation/components/compare/api';
import {
  getMatchModeLabel,
  isEmptyMode,
  CHARACTER_MATCH_OPTIONS,
  VERSION_MATCH_OPTIONS,
  CHARACTER_MATCH_ARRAY_OPTIONS,
  FieldModeOptionsMap,
} from '@/presentation/components/compare/mode-label';
import {
  CompareItem,
  CompareStore,
  isSameCompareItemIgnoreEmpty,
  oneDayLimit,
} from '@/presentation/components/compare/store';
import { newColumn, getColumn } from '@/presentation/pages/memory/analytics/trend/compare/column';
import { FieldKey, getLocalField, StandardCommonFieldKey, VERSION_MODE_OPTIONS } from '@/proto/field';
import { MetricSubType, MetricType } from '@/proto/metric';

export const getStoreKey = (metricType: MetricType, metricSubType?: MetricSubType) => `${metricType}-${metricSubType}`;

const conditionLabels: Record<string, string> = {
  [StandardCommonFieldKey.AppVersion]: i18n.t('APP版本'),
  [StandardCommonFieldKey.OsVersion]: i18n.t('系统版本'),
  [StandardCommonFieldKey.SdkVersion]: i18n.t('SDK版本'),
  [StandardCommonFieldKey.Model]: i18n.t('机型'),
  [StandardCommonFieldKey.ProcessName]: i18n.t('进程'),
  ['standard_is64bit']: i18n.t('32/64位'),
  ['standard_fd_type']: i18n.t('FD类型'),
  [StandardCommonFieldKey.BuildNumber]: i18n.t('构建号'),
  [StandardCommonFieldKey.AppVersionMode]: i18n.t('构建类型'),
  [StandardCommonFieldKey.BundleId]: 'Bundle ID',
  [StandardCommonFieldKey.ChannelId]: i18n.t('渠道'),
  [StandardCommonFieldKey.AbTestTag]: i18n.t('业务下钻'),
  [StandardCommonFieldKey.PssStage]: i18n.t('场景'),
};

const fieldModeOptions: FieldModeOptionsMap = {
  [StandardCommonFieldKey.AppVersion]: VERSION_MATCH_OPTIONS,
  [StandardCommonFieldKey.OsVersion]: VERSION_MATCH_OPTIONS,
  [StandardCommonFieldKey.SdkVersion]: VERSION_MATCH_OPTIONS,
  [StandardCommonFieldKey.BuildNumber]: CHARACTER_MATCH_OPTIONS,
  [StandardCommonFieldKey.BundleId]: CHARACTER_MATCH_OPTIONS,
  [StandardCommonFieldKey.ChannelId]: CHARACTER_MATCH_OPTIONS,
  [StandardCommonFieldKey.Model]: CHARACTER_MATCH_OPTIONS,
  [StandardCommonFieldKey.ProcessName]: CHARACTER_MATCH_OPTIONS,
  [StandardCommonFieldKey.PssStage]: CHARACTER_MATCH_OPTIONS,
  [StandardCommonFieldKey.AbTestTag]: CHARACTER_MATCH_ARRAY_OPTIONS,
};

const enumLabel = (key: string, v: any): string => {
  if (key === 'standard_is64bit') {
    if (v === '' || v === null || v === undefined) return i18n.t('所有');
    return (getLocalField(FieldKey.Bit)?.find(o => o.value === v)?.label as string) ?? v;
  }
  if (key === 'standard_fd_type') {
    if (v === '' || v === null || v === undefined) return i18n.t('所有');
    return (getLocalField(FieldKey.FdType)?.find(o => o.value === v)?.label as string) ?? v;
  }
  if (key === StandardCommonFieldKey.AppVersionMode) {
    if (v === '' || v === null || v === undefined) return i18n.t('所有');
    return (VERSION_MODE_OPTIONS.find(o => o.value === v)?.label as string) ?? v;
  }
  return v;
};

const getSimpleValue = (key: string, val: any): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) {
    return val.map(v => enumLabel(key, v)).join(',');
  }
  return '';
};

const getModeValue = (key: string, val: any): string => {
  if (!('values' in val) || val.values === undefined || val.values === null) {
    return '';
  }
  const modeLabel = getMatchModeLabel(val.mode, fieldModeOptions, key);
  let valuesText = '';
  if (typeof val.values === 'object' && val.values !== null && 'min' in val.values) {
    valuesText = `${val.values.min}~${val.values.max}`;
  } else if (Array.isArray(val.values)) {
    valuesText = val.values.map((v: any) => enumLabel(key, v)).filter((v: any) => v !== '')
      .join(',');
  } else if (typeof val.values === 'string') {
    valuesText = val.values;
  }
  return `(${modeLabel}) ${valuesText}`;
};

const getConditionValue = (key: string, val: any): string => {
  if (val === null || val === undefined) return '';
  if (Array.isArray(val?.conditions) && val.conditions.length > 0) {
    return val.conditions.map((cond: any) => {
      const modeLabel = getMatchModeLabel(cond.operator, fieldModeOptions, key);
      if (isEmptyMode(cond.operator)) {
        return `(${modeLabel})`;
      }
      let condValues: any[] = [];
      if (Array.isArray(cond.values)) {
        condValues = cond.values;
      } else if (cond.values) {
        condValues = [cond.values];
      }
      const valuesText = condValues.map((v: any) => enumLabel(key, v)).filter((v: any) => v !== '')
        .join(',');
      return `(${modeLabel}) ${valuesText}`;
    }).join('；');
  }
  if (isEmptyMode(val?.mode)) {
    return `(${getMatchModeLabel(val.mode, fieldModeOptions, key)})`;
  }
  if (Array.isArray(val?.values) && val?.values?.length === 0) return '';
  const simpleValue = getSimpleValue(key, val);
  if (simpleValue) return simpleValue;
  if (typeof val === 'object' && 'mode' in val) {
    return getModeValue(key, val);
  }
  return '';
};

const buildConditions = (fields: Record<string, any>): Array<{ label: string; value: string }> => (
  Object.entries(conditionLabels)
    .filter(([key]) => {
      const val = fields[key];
      return val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0);
    })
    .map(([key, label]) => ({
      label,
      value: getConditionValue(key, fields[key]),
    }))
    .filter(item => item.value !== '')
);

export const newAddToCompareList = (
  fields: Record<string, any>,
  metricType: MetricType,
  metricSubType?: MetricSubType,
  token?: string,
) => {
  const timeRange = fields[StandardCommonFieldKey.TimeRange] as { date_value?: moment.MomentInput[] } | undefined;
  const filterFields: Record<string, any> = {
    ...fields,
    [FieldKey.TimeRange]: timeRange?.date_value
      ?.map?.((item: any) => moment(item).format(DEFAULT_MINUTE_FORMATTER)),
  };
  const conditions = buildConditions(filterFields);
  filterFields._conditions = conditions;
  const item = {
    token,
    id: getCompareItemId(newColumn() as any, filterFields),
    fields: filterFields,
  };
  try {
    compareStore.addItem(getStoreKey(metricType, metricSubType), item, false);
  } catch (error: any) {
    message.warning(error.message);
  }
};

export const addToCompareList = (
  fields: Record<string, any>,
  metricType: MetricType,
  metricSubType?: MetricSubType,
) => {
  const filterFields = {
    ...fields,
    [FieldKey.TimeRange]: fields[FieldKey.TimeRange]
      .map((item: any) => moment(item).format(DEFAULT_FORMATTER)),
  };
  const item = {
    id: getColumnString(getColumn(metricType), filterFields),
    fields: filterFields,
  };
  try {
    compareStore.addItem(getStoreKey(metricType, metricSubType), item, false);
  } catch (error: any) {
    message.warning(error.message);
  }
};

export const compareStore = new CompareStore<CompareItem>({
  canAdd: oneDayLimit,
  isSame: isSameCompareItemIgnoreEmpty,
});
